// @ts-nocheck
/**
 * Phone layout pieces for the board: the compact top bar, the tab bar (a side rail in landscape),
 * the Person and brick panels that replace hover menus, and the card-based backlog.
 * Board.tsx owns all state and writes; these only render and call back.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Avatar, DiscIcon, discMeta } from "../lib/disciplines";
import { PriIcon, fmt, baseIdOf } from "./boardModel";
import { Hamburger, Plus } from "./Editors";

/** Phone-sized viewport: narrow, or short with a touch screen (a phone turned sideways). */
export function useViewport() {
  const read = () => ({ w: window.innerWidth, h: window.innerHeight });
  const [s, set] = useState(read);
  useEffect(() => {
    const f = () => set(read());
    window.addEventListener("resize", f);
    window.addEventListener("orientationchange", f);
    return () => { window.removeEventListener("resize", f); window.removeEventListener("orientationchange", f); };
  }, []);
  const coarse = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  const mobile = s.w <= 760 || (coarse && s.h <= 520);
  return { mobile, landscape: mobile && s.w > s.h, w: s.w, h: s.h };
}

export const zoomLabel = px => (px < 1 ? "Project" : px < 3 ? "Quarter" : px < 9 ? "Month" : px < 40 ? "Week" : "Day");

export const MOBILE_CSS = `
  .m-top{display:flex;align-items:center;gap:8px;padding:max(6px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right)) 6px max(12px, env(safe-area-inset-left));background:#151A21;flex-shrink:0}
  .m-proj{font:700 20px 'Barlow Condensed';letter-spacing:.07em;color:#E8A33D;text-transform:uppercase;flex:0 1 auto;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .m-ib{width:36px;height:36px;border-radius:9px;border:1px solid #364356;background:#232B36;color:#AEB9C8;display:grid;place-items:center;flex-shrink:0;cursor:pointer;padding:0}
  .m-ib:disabled{opacity:.35}
  .m-chips{display:flex;gap:6px;overflow-x:auto;scrollbar-width:none;padding:0 max(12px, env(safe-area-inset-right)) 8px max(12px, env(safe-area-inset-left));flex-shrink:0}
  .m-chips::-webkit-scrollbar{display:none}
  .m-chip{flex-shrink:0;display:flex;align-items:center;gap:6px;padding:6px 11px;border-radius:16px;border:1px solid #364356;background:#232B36;color:#8A97A8;font:600 12px 'Barlow Condensed';letter-spacing:.08em;text-transform:uppercase;cursor:pointer;white-space:nowrap}
  .m-chip.on{border-color:#E8A33D;color:#E8EDF3;background:#3A2F17}
  .m-chip i{width:8px;height:8px;border-radius:50%;display:block}
  .m-zoom{font:600 12px 'Barlow Condensed';letter-spacing:.1em;color:#8A97A8;text-transform:uppercase;padding:7px 9px;border:1px solid #364356;border-radius:9px;white-space:nowrap}
  .m-zoom b{color:#E8EDF3}
  .m-tabs{display:grid;grid-template-columns:repeat(4,1fr);border-top:1px solid #2E3947;background:#1B222C;padding:6px 4px max(8px, env(safe-area-inset-bottom));flex-shrink:0}
  .m-rail{display:flex;flex-direction:column;justify-content:center;gap:14px;width:calc(62px + env(safe-area-inset-left));box-sizing:border-box;border-right:1px solid #2E3947;background:#1B222C;flex-shrink:0;padding-left:env(safe-area-inset-left)}
  .m-tab{display:flex;flex-direction:column;align-items:center;gap:2px;font:600 10.5px 'Barlow Condensed';letter-spacing:.08em;text-transform:uppercase;color:#5F6B7C;background:none;border:none;cursor:pointer;padding:4px 0}
  .m-tab.on{color:#E8A33D}
  .vv-layer>*{pointer-events:auto}
  .m-back{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:150;animation:mFade .18s ease;touch-action:none}
  .m-sheet{position:fixed;left:0;right:0;bottom:0;max-height:78%;overflow:auto;overscroll-behavior:contain;transition:max-height .22s ease;background:#232B36;border-top:1px solid #364356;border-radius:18px 18px 0 0;z-index:160;padding:8px 16px max(16px, env(safe-area-inset-bottom));box-shadow:0 -12px 30px rgba(0,0,0,.45);display:flex;flex-direction:column;gap:12px;animation:mUp .24s cubic-bezier(.2,.9,.3,1.2)}
  .m-sheet.full{max-height:94%}
  .m-grabber{align-self:stretch;display:flex;justify-content:center;padding:6px 0 10px;margin:-8px -16px -6px;touch-action:none;cursor:grab}
  .m-side{position:fixed;top:0;right:0;bottom:0;width:min(320px,45%);overflow:auto;overscroll-behavior:contain;background:#232B36;border-left:1px solid #364356;z-index:160;padding:14px 16px 16px;padding-right:max(16px, env(safe-area-inset-right));box-shadow:-12px 0 30px rgba(0,0,0,.4);display:flex;flex-direction:column;gap:12px;animation:mSide .24s cubic-bezier(.2,.9,.3,1.1)}
  .m-grab{width:38px;height:5px;border-radius:3px;background:#5F6B7C;align-self:center;flex-shrink:0}
  .m-lbl{font:600 11px 'Barlow Condensed';letter-spacing:.14em;text-transform:uppercase;color:#8A97A8;margin-bottom:6px}
  .m-dchip{display:inline-flex;align-items:center;gap:5px;padding:4px 10px 4px 6px;border-radius:14px;background:#1B222C;border:1px solid #364356;font:600 12px 'Barlow Condensed';letter-spacing:.06em;text-transform:uppercase;color:#E8EDF3}
  .m-act{display:grid;grid-template-columns:repeat(auto-fit,minmax(0,1fr));gap:8px}
  .m-act button{height:44px;border-radius:10px;border:1px solid #364356;background:#1B222C;color:#E8EDF3;font:600 13px 'Barlow Condensed';letter-spacing:.1em;text-transform:uppercase;cursor:pointer}
  .m-act button.pri{background:#E8A33D;border-color:#E8A33D;color:#1a1d21}
  .m-range{width:100%;accent-color:#E8A33D;height:28px}
  .m-card{position:relative;border-radius:12px;background:#1B222C;border:1px solid #2E3947;padding:10px 12px 10px 15px;display:flex;flex-direction:column;gap:8px;overflow:hidden}
  .m-card::before{content:"";position:absolute;left:0;top:0;bottom:0;width:4px;background:var(--wc)}
  .m-est{display:flex;gap:5px;flex-wrap:wrap}
  .m-est span{display:flex;align-items:center;gap:4px;font:600 12px 'Barlow Condensed';padding:2px 7px 2px 5px;border-radius:4px;background:#232B36;color:#E8EDF3;font-variant-numeric:tabular-nums}
  .m-ini{display:flex;align-items:center;gap:10px;padding:11px 10px;border-radius:10px;background:#1B222C;border:1px solid #2E3947;cursor:pointer;font-size:15px;font-weight:600;color:#E8EDF3}
  .m-ini.on{border-color:#6B5228;background:#3A2F17}
  .m-ini small{margin-left:auto;font:600 12px 'Barlow Condensed';color:#5F6B7C}
  @keyframes mUp{from{transform:translateY(40%);opacity:.4}to{transform:none;opacity:1}}
  @keyframes mSide{from{transform:translateX(40%);opacity:.4}to{transform:none;opacity:1}}
  @keyframes mFade{from{opacity:0}to{opacity:1}}
`;

const ICON = {
  board: <path d="M3 6h9M7 12h11M5 18h8" />,
  backlog: <><rect x="4" y="4" width="16" height="5" rx="1.5" /><path d="M8 13h12M8 18h12M4 13h.5M4 18h.5" /></>,
  team: <><circle cx="9" cy="9" r="3.2" /><path d="M3.5 19c.8-3.2 3-5 5.5-5s4.7 1.8 5.5 5" /><circle cx="17" cy="8" r="2.4" /><path d="M16 13.2c2.4 0 4 1.6 4.6 4.3" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" /></>,
  undo: <><path d="M9 7L5 11l4 4" /><path d="M5 11h9a5 5 0 0 1 0 10h-2" /></>,
  back: <path d="M15 5l-7 7 7 7" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
};
export const Glyph = ({ k, size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} style={{ display: "block", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}>{ICON[k]}</svg>
);

export function MobileTop({ project, onBack, onUndo, canUndo, pods, hidden, togglePod, px, status, landscape, tab, laneMode, toggleLanes }) {
  const chips = tab === "board" && (
    <div className="m-chips" style={landscape ? { padding: 0, flex: 1, minWidth: 0 } : undefined}>
      <button className="m-chip on" onClick={toggleLanes} title="Switch swim lanes between people and initiatives"
        style={{ borderColor: "#5FD3BC", background: "#16302C" }}>⇄ {laneMode === "initiatives" ? "Initiatives" : "People"}</button>
      {pods.map(p => (
        <button key={p.id} className={"m-chip" + (hidden.includes(p.id) ? "" : " on")} onClick={() => togglePod(p.id)}>
          <i style={{ background: p.color }} />{p.name.split("·")[0].replace(/\bpod\b/i, "").trim() || p.name}
        </button>
      ))}
    </div>
  );
  return (
    <>
      <div className="m-top">
        <button className="m-ib" onClick={onBack} aria-label="All projects"><Glyph k="back" size={18} /></button>
        <div className="m-proj">{project.name}</div>
        {landscape ? chips : <div style={{ flex: 1 }} />}
        {status}
        {tab === "board" && <span className="m-zoom">Zoom <b>{zoomLabel(px)}</b></span>}
        <button className="m-ib" onClick={onUndo} disabled={!canUndo} aria-label="Undo"><Glyph k="undo" size={18} /></button>
      </div>
      {!landscape && chips}
    </>
  );
}

export function MobileNav({ tab, onTab, vertical }) {
  const items = [["board", "Board"], ["backlog", "Backlog"], ["team", "Team"], ["settings", "Settings"]];
  return (
    <nav className={vertical ? "m-rail" : "m-tabs"}>
      {items.map(([k, l]) => (
        <button key={k} className={"m-tab" + (tab === k ? " on" : "")} onClick={() => onTab(k)}>
          <Glyph k={k} size={22} />{l}
        </button>
      ))}
    </nav>
  );
}

/**
 * A layer pinned to what's actually on screen (Safari's visual viewport), so fixed UI inside it
 * (sheets, the tab bar, dialogs) always fits the screen at normal size, even if the page has been
 * zoomed or the browser bars are showing. Children use position: fixed relative to this layer.
 */
export function ScreenLayer({ children, z = 150 }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current, vv = window.visualViewport;
    if (!el || !vv) return;
    const fit = () => {
      const s = vv.scale || 1;
      el.style.left = vv.offsetLeft + "px";
      el.style.top = vv.offsetTop + "px";
      el.style.width = vv.width * s + "px";
      el.style.height = vv.height * s + "px";
      el.style.transform = `scale(${1 / s})`;
    };
    fit();
    vv.addEventListener("resize", fit);
    vv.addEventListener("scroll", fit);
    return () => { vv.removeEventListener("resize", fit); vv.removeEventListener("scroll", fit); };
  }, []);
  return createPortal(
    <div ref={ref} className="vv-layer" style={{ position: "fixed", left: 0, top: 0, width: "100%", height: "100%", transform: "scale(1)", transformOrigin: "0 0", pointerEvents: "none", zIndex: z }}>
      {children}
    </div>,
    document.body,
  );
}

export function Panel({ landscape, onClose, children }) {
  /* the tap that opened a panel also sends a click, which would land on the backdrop */
  const openedAt = useRef(Date.now());
  const sheetRef = useRef(null);
  const [full, setFull] = useState(false);
  useEffect(() => {
    const k = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  /* drag the handle: down far enough closes, up expands; the page behind never moves */
  const grab = e => {
    const el = sheetRef.current;
    if (!el) return;
    e.preventDefault();
    const y0 = e.clientY, id = e.pointerId;
    e.currentTarget.setPointerCapture?.(id);
    el.style.animation = "none";
    el.style.transition = "none";
    let dy = 0;
    const move = ev => {
      if (ev.pointerId !== id) return;
      dy = ev.clientY - y0;
      el.style.transform = `translateY(${dy > 0 ? dy : dy / 3}px)`;
    };
    const up = ev => {
      if (ev.pointerId !== id) return;
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up);
      el.style.transition = "transform .2s ease, max-height .22s ease";
      if (dy > 90) { el.style.transform = "translateY(100%)"; setTimeout(onClose, 160); return; }
      el.style.transform = "";
      if (dy < -30) setFull(true);
      else if (dy > 30) setFull(false);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };
  return (
    <ScreenLayer>
      <div className="m-back" onClick={() => { if (Date.now() - openedAt.current > 450) onClose(); }} style={landscape ? { background: "transparent" } : undefined} />
      <div ref={sheetRef} className={landscape ? "m-side" : "m-sheet" + (full ? " full" : "")} role="dialog">
        {!landscape && <div className="m-grabber" onPointerDown={grab} aria-label="Drag to resize or close"><div className="m-grab" /></div>}
        {landscape && <button className="m-ib" style={{ alignSelf: "flex-end", width: 32, height: 32 }} onClick={onClose} aria-label="Close"><Glyph k="close" size={16} /></button>}
        {children}
      </div>
    </ScreenLayer>
  );
}

const DiscChip = ({ dm, children }) => (
  <span className="m-dchip"><DiscIcon icon={dm.icon} size={14} color={dm.color} />{children ?? dm.label}</span>
);

export function PersonPanel({ nm, config, m, uOf, setUtil, resolved, nowDay, wsColor, onEdit, landscape, onClose }) {
  const r = config.resources.find(x => x.key === nm);
  const [u, setU] = useState(() => Math.round(uOf(nm) * 100));
  useEffect(() => setU(Math.round(uOf(nm) * 100)), [nm, uOf(nm)]);
  if (!r) return null;
  const prim = discMeta(config, r.discipline);
  const teams = config.teams.filter(t => t.members.includes(nm));
  const commit = () => { const v = u / 100; if (Math.abs(v - uOf(nm)) > 1e-6) setUtil(nm, v); };
  const seen = new Set();
  const next = (resolved[nm] || []).filter(s => s.d1 > nowDay && !s.ghost).sort((a, b) => a.d0 - b.d0)
    .filter(s => { const k = baseIdOf(s.id); if (seen.has(k)) return false; seen.add(k); return true; }).slice(0, 4);
  return (
    <Panel landscape={landscape} onClose={onClose}>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <Avatar name={r.name} disc={prim} size={60} />
        <div style={{ minWidth: 0 }}>
          <div style={{ font: "700 24px/1.05 'Barlow Condensed'", letterSpacing: ".03em" }}>{m.labelOf(nm)}</div>
          {m.labelOf(nm) !== r.name && <div style={{ color: "#AEB9C8", fontSize: 14, marginTop: 2 }}>{r.name}</div>}
          <div style={{ color: "#8A97A8", fontSize: 13, marginTop: 3 }}>{teams.map(t => t.name).join(" · ") || "No team"}{r.userId ? " · linked to a profile" : ""}</div>
        </div>
      </div>
      <div>
        <div className="m-lbl">Disciplines</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          <DiscChip dm={prim}>{prim.label} · primary</DiscChip>
          {(r.extraDisciplines || []).map(d => <DiscChip key={d} dm={discMeta(config, d)} />)}
        </div>
      </div>
      <div>
        <div className="m-lbl">Capacity {r.fte < 1 ? `· ${r.fte} FTE` : ""}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <input className="m-range" type="range" min={10} max={100} step={5} value={u} aria-label={`${r.name}'s capacity`}
            onChange={e => setU(+e.target.value)} onPointerUp={commit} onKeyUp={commit} onBlur={commit} />
          <b style={{ font: "700 20px 'Barlow Condensed'", width: 48, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{u}%</b>
        </div>
      </div>
      <div>
        <div className="m-lbl">Up next</div>
        {!next.length && <div style={{ color: "#5F6B7C", fontSize: 13 }}>Nothing scheduled.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {next.map(s => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: "#C3CCD8" }}>
              <i style={{ width: 10, height: 10, borderRadius: 3, background: wsColor[s.ws.trim()] || "#D3D0D0", flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.task}</span>
              <span style={{ color: "#8A97A8", font: "500 12.5px 'Barlow Condensed'", whiteSpace: "nowrap" }}>{fmt(m.dayToDate(s.d0))} → {fmt(m.dayToDate(Math.max(s.d0, s.d1 - 0.01)))}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="m-act"><button className="pri" onClick={onEdit}>Edit person</button></div>
    </Panel>
  );
}

export function BrickPanel({ nm, segId, config, m, resolved, wsColor, state, onPin, onEdit, onOpenInitiative, landscape, onClose }) {
  const segs = (resolved[nm] || []).filter(s => baseIdOf(s.id) === segId && !s.ghost);
  if (!segs.length) return null;
  const s = segs[0];
  const d0 = Math.min(...segs.map(x => x.d0)), d1 = Math.max(...segs.map(x => x.d1));
  const pd = segs.reduce((a, x) => a + x.pd, 0);
  const dm = discMeta(config, s.disc);
  const r = config.resources.find(x => x.key === nm);
  const epic = (state.backlog || []).find(t => t.n === s.task);
  return (
    <Panel landscape={landscape} onClose={onClose}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, font: "600 11.5px 'Barlow Condensed'", letterSpacing: ".1em", textTransform: "uppercase", color: "#8A97A8" }}>
        <i style={{ width: 9, height: 9, borderRadius: 2, background: wsColor[s.ws.trim()] || "#D3D0D0" }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.ws.trim()} · {m.priName(s.pri)}{s.isN ? " · out of budget" : ""}</span>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <span style={{ marginTop: 5 }}><PriIcon p={s.pri} size={14} model={m} /></span>
        <div style={{ font: "700 22px/1.1 'Barlow Condensed'", letterSpacing: ".02em" }}>{s.task}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 12px", alignItems: "center", fontSize: 14 }}>
        <span className="m-lbl" style={{ margin: 0 }}>Task</span>
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}><DiscChip dm={dm} /> {pd.toFixed(1)} person-days</span>
        <span className="m-lbl" style={{ margin: 0 }}>On</span>
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>{r && <Avatar name={r.name} disc={discMeta(config, r.discipline)} size={26} />}{m.labelOf(nm)}</span>
        <span className="m-lbl" style={{ margin: 0 }}>Dates</span>
        <span>{fmt(m.dayToDate(d0))} → {fmt(m.dayToDate(Math.max(d0, d1 - 0.01)))}{s.pinned ? " · pinned" : ""}</span>
      </div>
      <div className="m-act">
        <button onClick={() => onPin(s)}>{s.pinned ? "Unpin" : "Pin"}</button>
        <button onClick={() => onOpenInitiative(s.ws.trim())}>Initiative</button>
        {epic && <button className="pri" onClick={() => onEdit(s, pd)}>Edit</button>}
      </div>
    </Panel>
  );
}

/** Epics of one initiative from the backlog, plus bricks for anything not in it, ordered by priority then start. */
export function epicsOf(w, backlog, wsIndex, PRI) {
  const byName = new Map();
  for (const t of backlog || []) if (t.w.trim() === w) byName.set(t.n, { name: t.n, pri: t.p, inScope: t.s, item: t, entries: [] });
  for (const [task, entries] of Object.entries(wsIndex[w] || {})) {
    const e = byName.get(task) || { name: task, pri: entries[0].pri, inScope: true, item: null, entries: [] };
    e.entries = entries;
    byName.set(task, e);
  }
  const start = e => (e.entries.length ? Math.min(...e.entries.map(x => x.startDay)) : Infinity);
  return [...byName.values()].sort((a, b) => (PRI[a.pri] ?? 9) - (PRI[b.pri] ?? 9) || start(a) - start(b) || a.name.localeCompare(b.name));
}

export function BacklogView({ config, state, m, pods, displayOrg, wsColor, wsIndex, resolved, maxDay, nowDay, wsOfTeam, landscape, open, setOpen, setEdit }) {
  const [expanded, setExpanded] = useState(null);
  const backlog = state.backlog || [];
  /* when each epic's bricks start and end */
  const spans = useMemo(() => {
    const out = {};
    for (const segs of Object.values(resolved)) for (const s of segs) {
      if (s.ghost) continue;
      const k = s.ws.trim() + "\u0000" + s.task;
      const o = out[k] || (out[k] = { d0: Infinity, d1: 0 });
      o.d0 = Math.min(o.d0, s.d0); o.d1 = Math.max(o.d1, s.d1);
    }
    return out;
  }, [resolved]);
  const inScope = backlog.filter(t => t.s);
  const lastEnd = Object.values(spans).reduce((mx, s) => Math.max(mx, s.d1), 0);
  const byPod = pods.map(p => ({ p, n: inScope.filter(t => wsOfTeam(t.w.trim()) === p.id).length })).filter(x => x.n);

  const summary = (
    <div style={{ background: "#1B222C", border: "1px solid #2E3947", borderRadius: 12, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 7 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <b style={{ font: "700 15px 'Barlow Condensed'", letterSpacing: ".06em", textTransform: "uppercase" }}>In scope</b>
        <span style={{ fontSize: 12.5, color: "#8A97A8" }}>{inScope.length} epics in · {backlog.length - inScope.length} out{lastEnd ? ` · done ${fmt(m.dayToDate(lastEnd - 0.01))}` : ""}</span>
      </div>
      <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", gap: 2 }}>
        {byPod.map(({ p, n }) => <i key={p.id} title={`${p.name}: ${n}`} style={{ flex: n, background: p.color }} />)}
        {backlog.length - inScope.length > 0 && <i title="Out of scope" style={{ flex: backlog.length - inScope.length, background: "#2E3947" }} />}
      </div>
    </div>
  );

  const initList = (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {pods.map(pod => (
        <div key={pod.id} style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 6 }}>
          <div className="hov" style={{ display: "flex", alignItems: "center", font: "700 12px 'Barlow Condensed'", letterSpacing: ".12em", textTransform: "uppercase", color: pod.color, padding: "6px 2px 0" }}>
            <span style={{ flex: 1 }}>{pod.name}</span>
            <Hamburger title={`Edit team ${pod.name}`} onClick={() => setEdit({ kind: "team", mode: "edit", team: pod.id })} />
          </div>
          {(displayOrg[pod.id] || []).map(w => (
            <div key={w} className={"m-ini hov" + (open === w ? " on" : "")} onClick={() => { setOpen(w); setExpanded(null); }}>
              <i style={{ width: 12, height: 12, borderRadius: 3, background: wsColor[w], flexShrink: 0 }} />
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w}</span>
              <small>{backlog.filter(t => t.w.trim() === w).length}</small>
              <Hamburger title={`Edit initiative ${w}`} onClick={() => setEdit({ kind: "initiative", mode: "edit", name: w, team: pod.id })} />
            </div>
          ))}
          <Plus style={{ padding: "8px 10px", fontSize: 12 }} onClick={() => setEdit({ kind: "initiative", mode: "add", team: pod.id })}>Initiative</Plus>
        </div>
      ))}
      <Plus style={{ padding: "8px 10px", fontSize: 12 }} onClick={() => setEdit({ kind: "team", mode: "add" })}>Team</Plus>
    </div>
  );

  const card = ep => {
    const span = spans[open + "\u0000" + ep.name];
    const est = ep.item ? Object.entries(ep.item.e || {}) : Object.entries(ep.entries.reduce((a, e) => ({ ...a, [e.disc]: (a[e.disc] || 0) + e.pd }), {}));
    const people = [...new Set(ep.entries.map(e => e.person))];
    const isOpen = expanded === ep.name;
    return (
      <div key={ep.name} className="m-card hov" style={{ "--wc": wsColor[open], opacity: ep.inScope ? 1 : 0.6 }} onClick={() => setExpanded(isOpen ? null : ep.name)}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <span style={{ marginTop: 3 }}><PriIcon p={ep.pri} size={12} model={m} /></span>
          <span style={{ flex: 1, font: "600 15px/1.2 'Barlow Semi Condensed'" }}>{ep.name}{!ep.inScope && <span style={{ color: "#8A97A8", fontWeight: 400, fontSize: 12 }}> · out of scope</span>}</span>
          {ep.item && <Hamburger title={`Edit epic ${ep.name}`} onClick={() => setEdit({ kind: "epic", mode: "edit", epic: ep.name })} />}
        </div>
        {est.length > 0 && <div className="m-est">{est.map(([d, pd]) => { const dm = discMeta(config, d); return <span key={d} title={dm.label}><DiscIcon icon={dm.icon} size={13} color={dm.color} strokeWidth={2.4} />{+(+pd).toFixed(1)}d</span>; })}</div>}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {people.slice(0, 4).map(nm => { const r = config.resources.find(x => x.key === nm); return r ? <Avatar key={nm} name={r.name} disc={discMeta(config, r.discipline)} size={22} badge={false} /> : null; })}
          <div style={{ flex: 1, position: "relative", height: 12, marginLeft: 4 }}>
            <div style={{ position: "absolute", left: 0, right: 0, top: 5, height: 2, background: "#2E3947", borderRadius: 1 }} />
            {span && <div style={{ position: "absolute", top: 1, height: 10, borderRadius: 3, background: wsColor[open], left: `${(span.d0 / maxDay) * 100}%`, width: `${Math.max(1.5, ((span.d1 - span.d0) / maxDay) * 100)}%` }} />}
            <div style={{ position: "absolute", top: 0, width: 2, height: 12, background: "#5FD3BC", left: `${(nowDay / maxDay) * 100}%` }} />
          </div>
          <span style={{ font: "600 11px 'Barlow Condensed'", color: "#8A97A8", letterSpacing: ".04em", whiteSpace: "nowrap" }}>{span ? `done ${fmt(m.dayToDate(span.d1 - 0.01))}` : "not scheduled"}</span>
        </div>
        {isOpen && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, borderTop: "1px solid #2E3947", paddingTop: 8 }} onClick={e => e.stopPropagation()}>
            {[...ep.entries].sort((a, b) => a.startDay - b.startDay).map(e => {
              const dm = discMeta(config, e.disc);
              const r = config.resources.find(x => x.key === e.person);
              return (
                <div key={e.segId} className="hov" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, minHeight: 34 }}>
                  <DiscIcon icon={dm.icon} size={15} color={dm.color} />
                  <span style={{ width: 82, color: "#C3CCD8" }}>{dm.label}{e.num ? " " + e.num : ""}</span>
                  {r && <Avatar name={r.name} disc={discMeta(config, r.discipline)} size={22} badge={false} />}
                  <span style={{ flex: 1, minWidth: 0, color: "#8A97A8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.labelOf(e.person)}{e.pinned ? " · pinned" : ""}</span>
                  <span style={{ color: "#8A97A8", font: "500 12.5px 'Barlow Condensed'" }}>{e.pd.toFixed(1)}pd</span>
                  {ep.item && <Hamburger title={`Edit ${dm.label} task`} onClick={() => setEdit({ kind: "task", mode: "edit", epic: ep.name, disc: e.disc, person: e.person, pd: e.pd, ws: open, chunks: ep.entries.filter(x => x.disc === e.disc).length })} />}
                </div>
              );
            })}
            {!ep.entries.length && <div style={{ color: "#5F6B7C", fontSize: 13 }}>No tasks yet.</div>}
            {ep.item && <Plus style={{ padding: "8px 10px", fontSize: 12, alignSelf: "flex-start" }} onClick={() => setEdit({ kind: "task", mode: "add", epic: ep.name, ws: open })}>Task</Plus>}
          </div>
        )}
      </div>
    );
  };

  const epicList = open && (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div className="hov" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {!landscape && <button className="m-ib" style={{ width: 32, height: 32 }} onClick={() => setOpen(null)} aria-label="All initiatives"><Glyph k="back" size={16} /></button>}
        <i style={{ width: 12, height: 12, borderRadius: 3, background: wsColor[open], flexShrink: 0 }} />
        <span style={{ font: "700 19px 'Barlow Condensed'", letterSpacing: ".04em", textTransform: "uppercase", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{open}</span>
        <Hamburger title={`Edit initiative ${open}`} onClick={() => setEdit({ kind: "initiative", mode: "edit", name: open, team: wsOfTeam(open) })} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: landscape ? "repeat(auto-fill, minmax(250px, 1fr))" : "1fr", gap: 8, alignItems: "start" }}>
        {epicsOf(open, backlog, wsIndex, m.PRI).map(card)}
      </div>
      <Plus style={{ padding: "10px", fontSize: 12.5, textAlign: "center" }} onClick={() => setEdit({ kind: "epic", mode: "add", ws: open })}>Epic in {open}</Plus>
    </div>
  );

  if (landscape) {
    return (
      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div style={{ width: 240, flexShrink: 0, overflowY: "auto", borderRight: "1px solid #2E3947", background: "#181F28", padding: 10, display: "flex", flexDirection: "column", gap: 10 }}>
          {summary}{initList}
        </div>
        <div style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: "10px max(12px, env(safe-area-inset-right)) 16px 12px" }}>
          {epicList || <div style={{ color: "#8A97A8", padding: 20 }}>Pick an initiative.</div>}
        </div>
      </div>
    );
  }
  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
      {!open && summary}
      {open ? epicList : initList}
    </div>
  );
}
